import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

(function () {
  const STORAGE_KEY = 'psij_legal_consent';
  const CHECKBOX_SELECTOR = '#legal_consent_checkbox';
  const ERROR_SELECTOR = '#legal_consent_error';
  const CONTAINER_SELECTOR = '.legal-consent';

  function q(s, r = document) { return r.querySelector(s); }
  function qa(s, r = document) { return Array.from(r.querySelectorAll(s)); }

  function getStoredConsent() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function setStoredConsent(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    } catch (_) { /* ignore */ }
  }

  function showInlineError(root, show) {
    const container = q(CONTAINER_SELECTOR, root) || q(CONTAINER_SELECTOR);
    const error = q(ERROR_SELECTOR, root) || q(ERROR_SELECTOR);
    if (error) error.style.display = show ? 'block' : 'none';
    if (container) container.classList.toggle('legal-consent--error', !!show);
  }

  function focusCheckbox(root) {
    const chk = q(CHECKBOX_SELECTOR, root) || q(CHECKBOX_SELECTOR);
    if (!chk) return;
    try { chk.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* ignore */ }
    try { chk.focus({ preventScroll: true }); } catch (_) { chk.focus(); }
  }

  function setButtonsEnabled(enabled, root = document) {
    const checkoutBtns = qa('button[name="checkout"], button[type="submit"][name="checkout"], [data-checkout-button]', root);
    const dynContainers = qa('.shopify-payment-button, .additional-checkout-buttons', root);
    const dynButtons = qa('.shopify-payment-button__button, .additional-checkout-buttons button', root);

    checkoutBtns.forEach(btn => {
      if (!btn) return;
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', String(!enabled));
      btn.style.opacity = enabled ? '' : '0.5';
      btn.style.pointerEvents = enabled ? '' : 'none';
    });

    dynContainers.forEach(c => {
      if (!c) return;
      c.style.display = enabled ? '' : 'none';
    });

    dynButtons.forEach(btn => {
      if (!btn.__legalConsentBound) {
        btn.addEventListener('click', (e) => {
          if (!getStoredConsent()) {
            e.preventDefault();
            e.stopPropagation();
            const scope = btn.closest('.cart-drawer__dialog') || document;
            showInlineError(scope, true);
            focusCheckbox(scope);
          }
        }, true);
        btn.__legalConsentBound = true;
      }
    });
  }

  function applyStoredStateToCheckbox(root) {
    const checkbox = q(CHECKBOX_SELECTOR, root);
    if (!checkbox) return false;
    const stored = getStoredConsent();
    if (checkbox.checked !== stored) {
      checkbox.checked = stored;
    }
    return true;
  }

  function wireConsent(root = document) {
    const checkbox = q(CHECKBOX_SELECTOR, root);
    if (!checkbox) return;

    applyStoredStateToCheckbox(root);

    if (!checkbox.__legalConsentChangeBound) {
      checkbox.addEventListener('change', () => {
        const current = q(CHECKBOX_SELECTOR, root) || checkbox;
        setStoredConsent(current.checked);
        setButtonsEnabled(current.checked, root);
        if (current.checked) showInlineError(root, false);
      });
      checkbox.__legalConsentChangeBound = true;
    }

    setButtonsEnabled(checkbox.checked, root);

    qa('form[action="/checkout"], form[name="cart"], form[action="/cart"]', root).forEach(form => {
      if (!form.__legalConsentSubmitBound) {
        form.addEventListener('submit', (e) => {
          const current = q(CHECKBOX_SELECTOR, root) || q(CHECKBOX_SELECTOR);
          const accepted = current ? current.checked : getStoredConsent();
          if (!accepted) {
            e.preventDefault();
            e.stopPropagation();
            showInlineError(root, true);
            focusCheckbox(root);
          }
        });
        form.__legalConsentSubmitBound = true;
      }
    });
  }

  let rafScheduled = false;
  function scheduleWire(root) {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => {
      rafScheduled = false;
      wireConsent(root);
    });
  }

  const initAll = () => {
    const dlg = document.querySelector('.cart-drawer__dialog');
    const root = dlg || document;
    wireConsent(root);

    if (dlg && 'MutationObserver' in window) {
      if (!dlg.__legalConsentObserver) {
        const mo = new MutationObserver(() => scheduleWire(dlg));
        mo.observe(dlg, { childList: true, subtree: true });
        dlg.__legalConsentObserver = mo;
      }
    }
  };

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('cart-drawer:open', initAll);
  if (document.readyState !== 'loading') initAll();
})();
